import { commissionReductionTips } from '../data';

const howToDo: Record<string, string> = {
  'Ask for a volume discount':
    'Email your account manager with last 3 months of volume stats. Mention a competitor\'s offer if you have one.',
  'Open a PAMM or MAM account':
    'Ask if your broker offers MAM/PAMM structures. Pooled volume from all sub-accounts counts toward higher tiers.',
  'Switch to a different account type':
    'Compare the Professional or Institutional account tier on your broker\'s website. The higher minimum deposit is often the only barrier.',
  'Trade higher timeframes':
    'Move from M1/M5 to H1/D1 charts. Fewer trades × bigger positions = less commission per dollar of profit.',
  'Negotiate a rebate program':
    'Specifically ask: "Do you offer volume-based rebates instead of upfront commission discounts?" The structure matters.',
  'Bundle services for better rates':
    'Move your index and crypto CFD trading to the same broker. Combined monthly volume crosses tier thresholds faster.',
};

export default function HowToReduceCommissionSection() {
  return (
    <section className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Section header */}
        <div className="max-w-[640px] mb-12 md:mb-16">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            Six ways to pay less commission — starting today
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            The published rate is rarely the final rate. Here is what actually works — ranked by real-world impact, with the exact move to make.
          </p>
        </div>

        {/* Dark unified card — 2-column grid */}
        <div className="relative bg-foreground-950 rounded-card overflow-hidden">
          {/* Ambient glows */}
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-accent-500/6 rounded-full blur-[140px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-primary-500/4 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative p-6 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {commissionReductionTips.map((tip, idx) => {
                const isHigh = tip.impact === 'High';
                const dotColor = isHigh ? 'bg-accent-500' : 'bg-secondary-500';
                const impactBg = isHigh
                  ? 'bg-accent-500/10 text-accent-400 border-accent-500/15'
                  : 'bg-secondary-500/10 text-secondary-400 border-secondary-500/15';
                const numberColor = isHigh ? 'text-accent-400' : 'text-foreground-700';
                const barColor = isHigh ? 'bg-accent-500/20' : 'bg-secondary-500/10';
                const tipAction = howToDo[tip.title];

                return (
                  <div
                    key={tip.title}
                    className="group relative rounded-card border border-foreground-800 hover:border-foreground-700 transition-colors duration-300"
                  >
                    <div className="p-5 md:p-6">
                      {/* Number + Impact row */}
                      <div className="flex items-start justify-between mb-4">
                        <span
                          className={`text-[52px] md:text-[64px] font-[800] leading-none tracking-tighter ${numberColor}`}
                        >
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border whitespace-nowrap ${impactBg}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                          {tip.impact} Impact
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-[17px] md:text-[18px] font-[700] text-background-50 leading-snug mb-3">
                        {tip.title}
                      </h3>

                      {/* Description */}
                      <p className="text-[14px] leading-relaxed text-foreground-300 mb-4">
                        {tip.desc}
                      </p>

                      {/* Thin separator */}
                      <div className={`h-px ${barColor} mb-4`} />

                      {/* How to actually do it */}
                      {tipAction && (
                        <div className="flex items-start gap-2.5">
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-foreground-800 text-[10px] font-bold text-foreground-400 flex-shrink-0 mt-0.5">
                            ?
                          </span>
                          <p className="text-[13px] leading-relaxed text-foreground-400">
                            <span className="font-semibold text-foreground-300">How: </span>
                            {tipAction}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom CTA */}
            <div className="mt-10 text-center">
              <div className="inline-flex flex-col items-center gap-2 px-8 py-5 rounded-card bg-foreground-800/50 border border-foreground-700">
                <p className="text-[15px] leading-relaxed text-foreground-200 font-semibold">
                  The difference between $3.50/side and $2.00/side at 200 lots/month is $600/month — or $7,200/year.
                </p>
                <p className="text-[13px] text-foreground-500">
                  That is the cost of not asking for a better rate.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}